import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Dropdown, Menu, Modal, Typography, message } from "antd";
import ListTable from "common/ListTable";
import {
  STORE_REQUISITION_DELETE,
  STORE_REQUISITION_DOWNLOAD_PDF,
  STORE_REQUISITION_LIST,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import {
  STORE_REQUISITION_CREATE_PERMISSION,
  STORE_REQUISITION_DELETE_PERMISSION,
  STORE_REQUISITION_EDIT_PERMISSION,
  STORE_REQUISITION_VIEW_PERMISSION,
} from "routes/permissions";
import { cglFormatDate, hasPermission } from "utils/functions";
import { getStoreRequisitionMovementLabel } from "./constants";

const StoreRequisition = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;
  const { Title } = Typography;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllStoreRequisitions = useCallback(
    async (filterValues = {}) => {
      setLoading(true);
      const query = `${STORE_REQUISITION_LIST}?page=${currentPage}`;
      const response = await getData(query, false, filterValues);

      if (response?.data?.code === 200) {
        const paginated = response?.data?.data;
        setMetaData({
          current: paginated?.current_page,
          total: paginated?.total,
          pageSize: paginated?.per_page,
        });
        setTableData(paginated?.data || []);
      } else {
        message.error("Error getting store requisition data");
      }
      setLoading(false);
    },
    [currentPage],
  );

  useEffect(() => {
    const existingFilter =
      JSON.parse(localStorage.getItem("store_requisition_filter_value")) || {};
    getAllStoreRequisitions(existingFilter);
  }, [refresh, getAllStoreRequisitions]);

  const refetch = () => setRefresh((prev) => prev + 1);

  const changePage = (page) => {
    setCurrentPage(page);
    refetch();
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const response = await deleteData(`${STORE_REQUISITION_DELETE}/${id}`);
    if (response?.code === 200) {
      message.success("Store Requisition deleted successfully");
      refetch();
    } else {
      message.error("Failed to delete store requisition");
    }
    setLoading(false);
  };

  const handleDownloadPdf = async (record) => {
    try {
      const response = await getData(
        `${STORE_REQUISITION_DOWNLOAD_PDF}/${record.id}`,
        false,
        null,
        null,
        "arraybuffer",
      );
      const data = response?.data;
      const header =
        data && data.byteLength >= 4
          ? String.fromCharCode(...new Uint8Array(data.slice(0, 4)))
          : "";

      if (response?.status === 200 && header === "%PDF") {
        const srNo = record?.requisition_no || record.id;
        const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `store_requisition_${srNo}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        HandleNotification("error", "bottomRight", "Failed to download PDF", null);
      }
    } catch (error) {
      HandleNotification("error", "bottomRight", "Failed to download PDF", null);
    }
  };

  const rowMenu = (record) => (
    <Menu
      onClick={({ key, domEvent }) => {
        domEvent?.stopPropagation();
        if (key === "view" && hasPermission([STORE_REQUISITION_VIEW_PERMISSION])) {
          navigate(`/production/store-requisition/view/${record.id}`);
        }
        if (key === "edit" && hasPermission([STORE_REQUISITION_EDIT_PERMISSION])) {
          navigate(`/production/store-requisition/edit/${record.id}`);
        }
        if (key === "download_pdf" && hasPermission([STORE_REQUISITION_VIEW_PERMISSION])) {
          handleDownloadPdf(record);
        }
        if (key === "delete" && hasPermission([STORE_REQUISITION_DELETE_PERMISSION])) {
          Modal.confirm({
            title: "Delete this store requisition?",
            okText: "Delete",
            okType: "danger",
            onOk: () => handleDelete(record.id),
          });
        }
      }}
      items={[
        {
          key: "view",
          disabled: !hasPermission([STORE_REQUISITION_VIEW_PERMISSION]),
          icon: <EyeOutlined />,
          label: "View",
        },
        {
          key: "download_pdf",
          disabled: !hasPermission([STORE_REQUISITION_VIEW_PERMISSION]),
          icon: <FilePdfOutlined />,
          label: "Download PDF",
        },
        {
          key: "edit",
          disabled: !hasPermission([STORE_REQUISITION_EDIT_PERMISSION]),
          icon: <EditOutlined />,
          label: "Edit",
        },
        {
          key: "delete",
          disabled: !hasPermission([STORE_REQUISITION_DELETE_PERMISSION]),
          icon: <DeleteOutlined />,
          label: "Delete",
          danger: true,
        },
      ]}
    />
  );

  const columns = [
    {
      title: "Requisition No",
      dataIndex: "requisition_no",
      key: "requisition_no",
    },
    {
      title: "Buyer",
      dataIndex: ["buyer", "name"],
      key: "buyer",
    },
    {
      title: "Factory",
      dataIndex: ["sub_contract_factory", "name"],
      key: "sub_contract_factory",
    },
    {
      title: "Entry Date",
      dataIndex: "entry_date",
      key: "entry_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Movement Type",
      dataIndex: "movement_type",
      key: "movement_type",
      render: (value) => getStoreRequisitionMovementLabel(value),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Action",
      key: "action",
      width: 72,
      align: "center",
      render: (_, record) => (
        <Dropdown overlay={rowMenu(record)} trigger={["click"]}>
          <MoreOutlined style={{ fontSize: 18, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAllStoreRequisitions}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="store_requisition"
      />
    ),
    [showFilter, tableData, getAllStoreRequisitions],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              handleAddItemOpen={() =>
                navigate("/production/store-requisition/entry")
              }
              disabled={!hasPermission([STORE_REQUISITION_CREATE_PERMISSION])}
              addButtonText={
                hasPermission([STORE_REQUISITION_CREATE_PERMISSION])
                  ? "Add New"
                  : "No Permission"
              }
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
          }
          title={<Title level={5}>Store Requisition</Title>}
        />
      }
      content={
        <>
          {memoizedCustomFilter}
          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              dataSource={tableData}
              columns={columns}
              rowKey="id"
              pagination={{
                showSizeChanger: false,
                current: metaData?.current || currentPage,
                total: metaData?.total,
                pageSize: metaData?.pageSize || 10,
                onChange: changePage,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </div>
        </>
      }
    />
  );
};

export default StoreRequisition;
